import { Trans, useTranslation } from 'react-i18next';
import log from 'loglevel';
import { AvailablePlan } from '../../../../store/rtk-query/apis/account/dto/response/get-available-plans.response';
import { BillingPeriod } from '../../../../enums/billing-period.enum';
import STRING_KEYS from '../../../../language/keys';
import PerfectButton from '../../../Common/Buttons/perfect-base-button/perfect-base-button.component';
import { PerfectDivider } from '../../../Common/perfect-divider/perfect-divider.component';
import { CONFIG } from '../../../../config';
import { useCreateCheckoutSessionMutation } from '../../../../store/rtk-query/apis/payment/payment.toolkit-api';
import { useGetMyUserQuery } from '../../../../store/rtk-query/apis/user/user.toolkit-api';
import { PerfectRibbon } from '../../../Common/perfect-ribbon/PerfectRibbon';
import { AppRouting } from '../../../../services/appRoutingResolver';
import { WorkflowType } from '../../../../store/rtk-query/apis/payment/dto/request/create-checkout-session.request';
import {
  CoinIcon,
  CoinStackIcon,
  Description,
  LockIcon,
  MonthlyCredits,
  PlanCTAWrapper,
  PlanDetailsWrapper,
  PlanIcon,
  PriceAmount,
  PriceInterval,
  PriceWrapper,
  SeatIcon,
  Title,
  UpgradeNowButton,
  UserSeats,
  Wrapper,
} from './PricingPlanCard.styled';

export type PricingPlanCardProps = {
  plan: AvailablePlan;
  billingPeriod: BillingPeriod;
};

const logger = log.getLogger('PricingPlanCard');

export const PricingPlanCard = ({
  plan,
  billingPeriod,
}: PricingPlanCardProps): JSX.Element => {
  const { t: translate } = useTranslation();

  const { data: { accountId, id: userId } = {} } = useGetMyUserQuery();

  const [
    createCheckoutSession,
    { isLoading: isCreatingCheckoutSession },
  ] = useCreateCheckoutSessionMutation();

  const periodicPriceAmount =
    billingPeriod === BillingPeriod.Yearly ?
      Math.ceil(plan?.annualPrice / 12) :
      plan?.monthlyPrice;
  const priceAmount = plan?.isCustomPlan ?
    translate(STRING_KEYS.UPGRADE_PLAN_DIALOG.PLANS.CUSTOM_PRICE) :
    periodicPriceAmount;

  const priceInterval =
    plan?.userSeatsPlanLimit === 1 ?
      translate(
        STRING_KEYS.UPGRADE_PLAN_DIALOG.PLANS.PRICE_INTERVAL.PER_MONTH,
      ) :
      translate(
        STRING_KEYS.UPGRADE_PLAN_DIALOG.PLANS.PRICE_INTERVAL
          .PER_USER_PER_MONTH,
      );

  const monthlyCreditsLabel =
    plan?.userSeatsPlanLimit === 1 ? (
      <Trans
        i18nKey={ STRING_KEYS.UPGRADE_PLAN_DIALOG.PLANS.CREDITS.MONTHLY_CREDITS }
        values={ { monthlyCredits: plan?.monthlyCredits } }
      />
    ) : (
      <Trans
        i18nKey={
          STRING_KEYS.UPGRADE_PLAN_DIALOG.PLANS.CREDITS.MONTHLY_CREDITS_PER_USER
        }
        values={ { monthlyCredits: plan?.monthlyCredits } }
      />
    );

  const userSeatsLabel =
    plan?.userSeatsPlanLimit === 1 ? (
      <Trans
        i18nKey={ STRING_KEYS.UPGRADE_PLAN_DIALOG.PLANS.USER_SEATS.ONE_USER }
      />
    ) : (
      <Trans
        i18nKey={ STRING_KEYS.UPGRADE_PLAN_DIALOG.PLANS.USER_SEATS.PAY_PER_USER }
      />
    );

  const handleContactSales = () => {
    window.open(CONFIG.CONTACT_SALES_URL, '_blank');
  };

  const handleUpgradeNow = async () => {
    try {
      const callbackUrl = new URL(window.location.href);
      callbackUrl.searchParams.set(
        AppRouting.URL_PARAMS.CHECKOUT_TYPE,
        'subscription',
      );

      const { url } = await createCheckoutSession({
        accountId,
        userId,
        planDefinitionId: plan?.id,
        billingPeriod,
        workflowType: WorkflowType.InAppPlanPurchase,
        callbackUrl: callbackUrl.toString(),
      }).unwrap();
      window.open(url, '_self');
    } catch (e) {
      logger.error(e);
    }
  };

  return (
    <Wrapper>
      {CONFIG.MOST_POPULAR_RECRUITER_PLAN_DEFINITION_ID === plan?.id && (
        <PerfectRibbon position="top-right">Most Popular</PerfectRibbon>
      )}

      <PlanDetailsWrapper>
        {plan?.imageUrl && <PlanIcon alt="icon" src={ plan?.imageUrl } />}

        <Title>{plan?.displayName}</Title>

        <Description>{plan?.description}</Description>

        <PriceWrapper>
          <PriceAmount>${priceAmount}</PriceAmount>

          <PriceInterval>
            {priceInterval}
          </PriceInterval>
        </PriceWrapper>
      </PlanDetailsWrapper>

      <PlanCTAWrapper>
        {plan?.isCustomPlan ? (
          <PerfectButton onClick={ handleContactSales }>
            {translate(STRING_KEYS.UPGRADE_PLAN_DIALOG.PLANS.CONTACT_SALES)}
          </PerfectButton>
        ) : (
          <UpgradeNowButton
            onClick={ handleUpgradeNow }
            isLoading={ isCreatingCheckoutSession }
          >
            {translate(STRING_KEYS.UPGRADE_PLAN_DIALOG.PLANS.UPGRADE_NOW)}
          </UpgradeNowButton>
        )}

        <PerfectDivider />

        <MonthlyCredits>
          {plan?.isCustomPlan ? <CoinStackIcon /> : <CoinIcon />}

          {plan?.isCustomPlan ? (
            <Trans
              i18nKey={
                STRING_KEYS.UPGRADE_PLAN_DIALOG.PLANS.CREDITS.CUSTOM_CREDITS
              }
            />
          ) : (
            monthlyCreditsLabel
          )}
        </MonthlyCredits>

        <UserSeats>
          {plan?.userSeatsPlanLimit === 1 ? <LockIcon /> : <SeatIcon />}

          {plan?.isCustomPlan ? (
            <Trans
              i18nKey={
                STRING_KEYS.UPGRADE_PLAN_DIALOG.PLANS.USER_SEATS.UNLIMITED
              }
            />
          ) : (
            userSeatsLabel
          )}
        </UserSeats>
      </PlanCTAWrapper>
    </Wrapper>
  );
};
